import React, { useEffect, useState } from "react";

// ──────────────────────────────────────────────────────
// AdminApiUsage.jsx — 공개 API v1 키별 사용량 (유료화 과금 근거 조회)
//   GET /api/v1/admin/usage?days=N  (admin_token Bearer — AdminClassifyHistory 패턴)
//   사용자·그룹·키(prefix)·이름 → 오늘/기간 items · 요청수 · 429 · 마지막 사용 · 상태.
// ──────────────────────────────────────────────────────

const fmtDateTime = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v).slice(0, 16);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch {
    return String(v).slice(0, 16);
  }
};

export default function AdminApiUsage({ API_BASE_URL }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [userFilter, setUserFilter] = useState("");

  const load = () => {
    setLoading(true);
    setError(null);
    const adminToken = localStorage.getItem("admin_token");
    fetch(`${API_BASE_URL}/api/v1/admin/usage?days=${days}`, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status)),
      )
      .then((d) => setRows(Array.isArray(d?.data) ? d.data : []))
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [API_BASE_URL, days]); // eslint-disable-line react-hooks/exhaustive-deps

  const uf = userFilter.trim().toLowerCase();
  const filtered = uf
    ? rows.filter(
        (r) =>
          String(r.user_id || "").toLowerCase().includes(uf) ||
          String(r.group_id || "").toLowerCase().includes(uf) ||
          String(r.name || "").toLowerCase().includes(uf),
      )
    : rows;

  const sum = filtered.reduce(
    (a, r) => ({
      today: a.today + (r.today_items || 0),
      period: a.period + (r.period_items || 0),
      req: a.req + (r.period_requests || 0),
      r429: a.r429 + (r.period_429 || 0),
    }),
    { today: 0, period: 0, req: 0, r429: 0 },
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-800">
          API 사용량{" "}
          <span className="text-sm font-normal text-gray-400">
            공개 API v1 키별 집계 — 무료 베타 사용량(유료화 과금 근거)
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <input
            className="border rounded px-2 py-1 text-sm w-44"
            placeholder="사용자/그룹/키이름 필터"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
          />
          <select
            className="border rounded px-2 py-1 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={7}>최근 7일</option>
            <option value={30}>최근 30일</option>
            <option value={90}>최근 90일</option>
          </select>
          <button
            onClick={load}
            className="border rounded px-3 py-1 text-sm hover:bg-gray-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-3 mb-3">
          {error} — 관리자 로그인(admin_token)이 필요합니다.
        </div>
      )}
      {loading ? (
        <div className="text-sm text-gray-400 p-4">불러오는 중…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border-t">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50">
                <th className="px-3 py-2">사용자</th>
                <th className="px-3 py-2">그룹</th>
                <th className="px-3 py-2">키</th>
                <th className="px-3 py-2">이름</th>
                <th className="px-3 py-2 text-right">오늘 items</th>
                <th className="px-3 py-2 text-right">{days}일 items</th>
                <th className="px-3 py-2 text-right">요청수</th>
                <th className="px-3 py-2 text-right">429</th>
                <th className="px-3 py-2">마지막 사용</th>
                <th className="px-3 py-2">상태</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                    발급된 키가 없습니다.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b hover:bg-gray-50 ${r.revoked_at ? "opacity-45" : ""}`}
                >
                  <td className="px-3 py-2">{r.user_id}</td>
                  <td className="px-3 py-2">{r.group_id || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.key_prefix}…</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-right">{r.today_items}</td>
                  <td className="px-3 py-2 text-right font-semibold">{r.period_items}</td>
                  <td className="px-3 py-2 text-right">{r.period_requests}</td>
                  <td className="px-3 py-2 text-right text-amber-600">{r.period_429 || 0}</td>
                  <td className="px-3 py-2 text-gray-500">{fmtDateTime(r.last_used_at)}</td>
                  <td className="px-3 py-2">{r.revoked_at ? "폐기됨" : "활성"}</td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-semibold bg-gray-50">
                  <td className="px-3 py-2" colSpan={4}>합계 ({filtered.length}개 키)</td>
                  <td className="px-3 py-2 text-right">{sum.today}</td>
                  <td className="px-3 py-2 text-right">{sum.period}</td>
                  <td className="px-3 py-2 text-right">{sum.req}</td>
                  <td className="px-3 py-2 text-right text-amber-600">{sum.r429}</td>
                  <td className="px-3 py-2" colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
